// Agent agreement (Audit page, admin only; 2026-10-23,
// docs/agent-draft-shadow.md, The agreement measure). Per intent, the last 7
// or 30 days: decided contacts with an agent draft, the median edit ratio of
// the agent's reply to what was sent, the share sent with small edits, step
// agreement (type matched / every key choice matched), the most-changed
// fields, the same by the agent's confidence level, and a plain "looks ready
// to try as the main draft" hint. A quality measure for the AI lane: no
// per-agent or per-person numbers. The hint switches nothing. Patrick owns
// the reporting views; this is a plain table until he designs it.
import { Fragment, useEffect, useState } from 'react'
import { Alert, Box, CircularProgress, Stack, Table, TableBody, TableCell, TableHead, TableRow, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { fetchShadowAgreement as realFetch } from './api.js'
import { agreementPct, agreementRatio, changedFieldsLine, CONFIDENCE_ORDER } from './shadowAgreement.js'

export default function ShadowAgreementPanel({ fetchAgreement = realFetch }) {
  const [state, setState] = useState({ data: null, error: null })
  const [days, setDays] = useState(30)
  useEffect(() => {
    let live = true
    fetchAgreement().then((data) => live && setState({ data, error: null })).catch((error) => live && setState({ data: null, error }))
    return () => { live = false }
  }, [fetchAgreement])
  if (state.error) return <Alert severity="error">Could not load the agent agreement: {state.error.message}</Alert>
  if (!state.data) return <CircularProgress size={24} />
  const c = state.data.constants ?? {}
  const window = (state.data.windows ?? []).find((w) => w.days === days) ?? { intents: [] }
  return (
    <Stack spacing={1} data-testid="shadow-agreement">
      <Typography variant="body2" color="text.secondary">
        How the agent's drafts compared with what was sent and processed, once each contact was decided. A quality measure for the AI
        lane, not a people report: no per-agent numbers. "Looks ready" needs at least {c.READY_MIN_CONTACTS} contacts, a median edit
        ratio of at most {c.READY_MAX_MEDIAN_EDIT_RATIO} and full step agreement of at least {agreementPct(c.READY_MIN_FULL_STEP_AGREEMENT)}.
        It is a hint only: nothing switches by itself.
      </Typography>
      <ToggleButtonGroup size="small" exclusive value={days} onChange={(e, v) => { if (v) setDays(v) }} aria-label="Window">
        {(state.data.windows ?? []).map((w) => <ToggleButton key={w.days} value={w.days} data-testid={`shadow-agreement-days-${w.days}`}>Last {w.days} days</ToggleButton>)}
      </ToggleButtonGroup>
      {!window.intents.length ? (
        <Typography variant="body2" data-testid="shadow-agreement-empty">No decided contacts with an agent draft in this window.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Intent</TableCell><TableCell align="right">Contacts</TableCell><TableCell align="right">Median edit ratio</TableCell>
              <TableCell align="right">Small edits</TableCell><TableCell align="right">Steps: type matched</TableCell><TableCell align="right">Steps: all choices</TableCell>
              <TableCell>Most changed</TableCell><TableCell>Hint</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {window.intents.map((r) => (
              <Fragment key={r.intent}>
                <TableRow data-testid="shadow-agreement-row" data-intent={r.intent}>
                  <TableCell>{r.intent}</TableCell>
                  <TableCell align="right">{r.contacts}</TableCell>
                  <TableCell align="right">{agreementRatio(r.medianEditRatio)}</TableCell>
                  <TableCell align="right">{agreementPct(r.smallEditShare)}</TableCell>
                  <TableCell align="right">{agreementPct(r.stepTypeAgreement)}</TableCell>
                  <TableCell align="right">{agreementPct(r.stepFullAgreement)}</TableCell>
                  <TableCell>{changedFieldsLine(r.mostChangedFields)}</TableCell>
                  <TableCell data-testid="shadow-agreement-hint">{r.ready ? r.hint : '—'}</TableCell>
                </TableRow>
                {CONFIDENCE_ORDER.filter((level) => r.byConfidence?.[level]).map((level) => {
                  const b = r.byConfidence[level]
                  return (
                    <TableRow key={`${r.intent}-${level}`} data-testid="shadow-agreement-confidence-row" sx={{ '& td': { color: 'text.secondary', fontSize: 12 } }}>
                      <TableCell sx={{ pl: 4 }}>Confidence {level}</TableCell>
                      <TableCell align="right">{b.contacts}</TableCell>
                      <TableCell align="right">{agreementRatio(b.medianEditRatio)}</TableCell>
                      <TableCell align="right">{agreementPct(b.smallEditShare)}</TableCell>
                      <TableCell align="right">{agreementPct(b.stepTypeAgreement)}</TableCell>
                      <TableCell align="right">{agreementPct(b.stepFullAgreement)}</TableCell>
                      <TableCell /><TableCell />
                    </TableRow>
                  )
                })}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      )}
      <Box />
    </Stack>
  )
}
